import axios from 'axios';
import { apiClient } from './apiClient';
import { getApiBaseUrl } from './apiConfig';

export type CannedResponse = {
  id: string;
  title: string;
  body: string;
  tags?: string | null;
  serviceCatalogItemId?: string | null;
  serviceCatalogItemName?: string | null;
  includeInAiSuggest: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type CannedResponseUpsert = {
  title: string;
  body: string;
  tags?: string | null;
  serviceCatalogItemId?: string | null;
  includeInAiSuggest: boolean;
  isActive: boolean;
  sortOrder: number;
};

export type SurveyQuestion = {
  id: string;
  prompt: string;
  type: 'Stars' | 'NumericScale' | 'LabeledScale' | string;
  min: number;
  max: number;
  labels: string[];
  allowComment: boolean;
  required: boolean;
};

export type SurveyFormCompany = {
  id: string;
  name: string;
};

export type SurveyForm = {
  id: string;
  name: string;
  surveyType: string;
  companyIds: string[];
  companies: SurveyFormCompany[];
  questions: SurveyQuestion[];
  allowGeneralComment: boolean;
  emailTemplateId?: string | null;
  emailTemplateName?: string | null;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type SurveyFormUpsert = {
  name: string;
  surveyType: string;
  companyIds: string[];
  questions: SurveyQuestion[];
  allowGeneralComment: boolean;
  emailTemplateId: string;
  isActive: boolean;
};

export type SurveyFormTestPreview = {
  token: string;
  relativePath: string;
  surveyUrl: string;
  expiresAtUtc: string;
  publicAppBaseUrlConfigured: boolean;
  warnings: string[];
};

export type SurveyFormTestSendResult = SurveyFormTestPreview & {
  recipientEmail: string;
  emailSent: boolean;
};

export type AccountHealthCompany = {
  companyId: string;
  companyName: string;
  healthScore: number;
  openTicketCount: number;
  slaBreachCount: number;
  avgCsat?: number | null;
  csatCount: number;
  avgNps?: number | null;
  npsCount: number;
  nearestContractEndUtc?: string | null;
  daysUntilContractEnd?: number | null;
};

export type SurveySummary = {
  avgCsat?: number | null;
  csatCount: number;
  avgNps?: number | null;
  npsCount: number;
};

export type AccountHealthListResult = {
  items: AccountHealthCompany[];
  totalCount: number;
  page: number;
  pageSize: number;
  surveySummary: SurveySummary;
};

export type PublicSurvey = {
  token: string;
  surveyType: string;
  minScore: number;
  maxScore: number;
  questions: SurveyQuestion[];
  allowGeneralComment: boolean;
  ticketCode?: string | null;
  companyName?: string | null;
  redirectUrl?: string | null;
  alreadyResponded: boolean;
  isExpired: boolean;
};

export type PublicSurveyAnswer = {
  questionId: string;
  score: number;
  comment?: string;
};

export async function listCannedResponses(ticketId?: string): Promise<CannedResponse[]> {
  const response = await apiClient.get<CannedResponse[]>('/v1/customer-success/canned-responses', {
    params: ticketId ? { ticketId } : undefined,
  });
  return response.data;
}

export async function listDeskCannedResponses(
  ticketId: string,
  options?: { signal?: AbortSignal },
): Promise<CannedResponse[]> {
  const response = await apiClient.get<CannedResponse[]>(
    `/v1/customer-success/desk/tickets/${ticketId}/canned-responses`,
    { signal: options?.signal },
  );
  return response.data;
}

export async function createCannedResponse(payload: CannedResponseUpsert): Promise<CannedResponse> {
  const response = await apiClient.post<CannedResponse>('/v1/customer-success/canned-responses', payload);
  return response.data;
}

export async function updateCannedResponse(
  id: string,
  payload: CannedResponseUpsert,
): Promise<CannedResponse> {
  const response = await apiClient.put<CannedResponse>(
    `/v1/customer-success/canned-responses/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteCannedResponse(id: string): Promise<void> {
  await apiClient.delete(`/v1/customer-success/canned-responses/${id}`);
}

export async function listAccountHealth(params?: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<AccountHealthListResult> {
  const response = await apiClient.get<AccountHealthListResult>('/v1/customer-success/health', {
    params: {
      search: params?.search,
      page: params?.page ?? 1,
      pageSize: params?.pageSize ?? 10,
    },
  });
  return response.data;
}

export async function fetchPublicSurvey(token: string): Promise<PublicSurvey> {
  const response = await axios.get<PublicSurvey>(`${getApiBaseUrl()}/v1/public/surveys/${token}`);
  return response.data;
}

export async function submitPublicSurvey(
  token: string,
  payload: { answers: PublicSurveyAnswer[]; comment?: string },
): Promise<void> {
  await axios.post(`${getApiBaseUrl()}/v1/public/surveys/${token}`, payload);
}

export async function listSurveyForms(): Promise<SurveyForm[]> {
  const response = await apiClient.get<SurveyForm[]>('/v1/customer-success/survey-forms');
  return response.data.map((item) => ({
    ...item,
    companyIds: item.companyIds ?? item.companies?.map((company) => company.id) ?? [],
    companies: item.companies ?? [],
  }));
}

export async function createSurveyForm(payload: SurveyFormUpsert): Promise<SurveyForm> {
  const response = await apiClient.post<SurveyForm>('/v1/customer-success/survey-forms', payload);
  return response.data;
}

export async function updateSurveyForm(id: string, payload: SurveyFormUpsert): Promise<SurveyForm> {
  const response = await apiClient.put<SurveyForm>(`/v1/customer-success/survey-forms/${id}`, payload);
  return response.data;
}

export async function deleteSurveyForm(id: string): Promise<void> {
  await apiClient.delete(`/v1/customer-success/survey-forms/${id}`);
}

export async function createSurveyFormTestPreview(
  id: string,
  payload?: { email?: string },
): Promise<SurveyFormTestPreview> {
  const response = await apiClient.post<SurveyFormTestPreview>(
    `/v1/customer-success/survey-forms/${id}/test-preview`,
    payload ?? {},
  );
  return response.data;
}

export async function sendSurveyFormTest(
  id: string,
  payload?: { email?: string },
): Promise<SurveyFormTestSendResult> {
  const response = await apiClient.post<SurveyFormTestSendResult>(
    `/v1/customer-success/survey-forms/${id}/test-send`,
    payload ?? {},
  );
  return response.data;
}

export type CommunicationAutomation = {
  id: string;
  name: string;
  emailTemplateId: string;
  emailTemplateName?: string | null;
  triggerType: string;
  delayMinutes: number;
  frequency?: string | null;
  surveyFormId?: string | null;
  surveyFormName?: string | null;
  companyId?: string | null;
  companyName?: string | null;
  communicationRoleId?: string | null;
  communicationRoleName?: string | null;
  includeTicketContact: boolean;
  isEnabled: boolean;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type CommunicationAutomationUpsert = {
  name: string;
  emailTemplateId: string;
  triggerType: string;
  delayMinutes: number;
  frequency?: string | null;
  surveyFormId?: string | null;
  companyId?: string | null;
  communicationRoleId?: string | null;
  includeTicketContact?: boolean;
  isEnabled: boolean;
};

export async function listCommunicationAutomations(): Promise<CommunicationAutomation[]> {
  const response = await apiClient.get<CommunicationAutomation[]>('/v1/customer-success/automations');
  return response.data;
}

export async function createCommunicationAutomation(
  payload: CommunicationAutomationUpsert,
): Promise<CommunicationAutomation> {
  const response = await apiClient.post<CommunicationAutomation>(
    '/v1/customer-success/automations',
    payload,
  );
  return response.data;
}

export async function updateCommunicationAutomation(
  id: string,
  payload: CommunicationAutomationUpsert,
): Promise<CommunicationAutomation> {
  const response = await apiClient.put<CommunicationAutomation>(
    `/v1/customer-success/automations/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteCommunicationAutomation(id: string): Promise<void> {
  await apiClient.delete(`/v1/customer-success/automations/${id}`);
}

export async function enqueueCommunicationAutomation(
  id: string,
  payload?: { companyId?: string; contractId?: string; ticketId?: string },
): Promise<void> {
  await apiClient.post(`/v1/customer-success/automations/${id}/enqueue`, payload ?? {});
}
